/**
 * The offline demo's scenarios.
 *
 * Mirrors the interaction contract of the agent's REST API — conversation →
 * message with per-turn read/write permission → async job emitting tool-call
 * progress → assistant reply carrying the new page state — without talking to
 * any backend. Mutations are computed client-side on the raw Volto form data,
 * so the demo works on whatever page is open.
 *
 * This module is the scenario engine only; `MockAgentClient` is what the UI
 * sees, and it is an `AgentClient` like any other.
 */

const STEP_DELAY = 1100;
const TOKEN_DELAY = 22;

const uid = () =>
  typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `id-${Math.random().toString(36).slice(2)}-${Date.now().toString(36)}`;

// ---------------------------------------------------------------------------
// Volto form data helpers
// ---------------------------------------------------------------------------

const slateBlock = (text, type = 'p') => ({
  '@type': 'slate',
  value: [{ type, children: [{ text }] }],
  plaintext: text,
});

const blockText = (block) => {
  if (!block) return '';
  if (block.plaintext) return block.plaintext;
  const walk = (nodes) =>
    (nodes || [])
      .map((node) =>
        typeof node.text === 'string' ? node.text : walk(node.children),
      )
      .join('');
  if (block.value) return walk(block.value);
  return block.title || block.description || '';
};

const layoutItems = (state) => state?.blocks_layout?.items || [];

const withBlocks = (state, blocks, items) => ({
  ...state,
  blocks,
  blocks_layout: { ...state.blocks_layout, items },
});

// Index of the first block after the page header (title/description).
const firstBodyIndex = (state, items) => {
  const headerTypes = new Set(['title', 'description']);
  const index = items.findIndex(
    (id) => !headerTypes.has(state.blocks[id]?.['@type']),
  );
  return index === -1 ? items.length : index;
};

const insertBlockAt = (state, block, index) => {
  const id = uid();
  const items = [...layoutItems(state)];
  items.splice(index, 0, id);
  return {
    id,
    state: withBlocks(state, { ...state.blocks, [id]: block }, items),
  };
};

const insertBlock = (state, block, position = 'bottom') =>
  insertBlockAt(
    state,
    block,
    position === 'top'
      ? firstBodyIndex(state, layoutItems(state))
      : layoutItems(state).length,
  );

const removeBlock = (state, blockId) => {
  const blocks = { ...state.blocks };
  delete blocks[blockId];
  return withBlocks(
    state,
    blocks,
    layoutItems(state).filter((id) => id !== blockId),
  );
};

// placement: 'top' | 'bottom' | 'up' | 'down'. "Top" means right below the
// page header (title/description), which stays in place; up/down move one
// position.
const moveBlock = (state, blockId, placement) => {
  const items = layoutItems(state);
  const from = items.indexOf(blockId);
  const without = items.filter((id) => id !== blockId);
  const min = firstBodyIndex(state, without);
  let insertAt;
  if (placement === 'top') insertAt = min;
  else if (placement === 'up') insertAt = Math.max(min, from - 1);
  else if (placement === 'down') insertAt = Math.min(without.length, from + 1);
  else insertAt = without.length;
  const next = [...without];
  next.splice(insertAt, 0, blockId);
  return withBlocks(state, state.blocks, next);
};

const cleanupText = (text) => {
  let next = text
    .replace(/\s+([,.;:!?])/g, '$1')
    .replace(/ {2,}/g, ' ')
    .trim();
  next = next.charAt(0).toUpperCase() + next.slice(1);
  if (!/[.!?:]$/.test(next)) next += '.';
  return next;
};

// A deterministic stand-in for an LLM copyedit: standard tightening rules
// (contractions, wordy phrases, dated spellings, filler adverbs). Most of
// the text stays untouched, so the resulting diff is scattered word-level
// changes with unchanged runs in between — the pattern a real editing
// agent produces on text that is already mostly fine.
const COPY_EDITS = [
  [/\bweb site\b/gi, 'website'],
  [/\bin order to\b/gi, 'to'],
  [/\bone or more of\b/gi, 'any of'],
  [/\bnewly created\b/gi, 'new'],
  [/\bis able to\b/gi, 'can'],
  [/\bat this point in time\b/gi, 'now'],
  [/\bmade possible only through\b/gi, 'made possible by'],
  [/\bdo not\b/gi, "don't"],
];

// Replacements inherit the casing of what they replace.
const matchCase = (replacement, matched) =>
  /^[A-Z]/.test(matched)
    ? replacement.charAt(0).toUpperCase() + replacement.slice(1)
    : replacement;

// Filler adverbs are dropped — unless the removal would break article
// agreement ("a important point"), in which case the adverb stays.
const FILLER_ADVERB =
  /(\b[Aa]n?\s+)?\b(?:very|really|easily|highly)\s+(?=(\w))/g;

const copyEditText = (text) => {
  let edited = COPY_EDITS.reduce(
    (acc, [pattern, replacement]) =>
      acc.replace(pattern, (matched) => matchCase(replacement, matched)),
    text,
  );
  edited = edited.replace(FILLER_ADVERB, (matched, article, next) => {
    if (!article) return '';
    const wantsAn = /^[aeiou]/i.test(next);
    const hasAn = /^an\s/i.test(article);
    return wantsAn === hasAn ? article : matched;
  });
  // An edit at a sentence start must not leave the sentence lowercase.
  return edited.replace(
    /(^|[.!?]\s+)([a-z])/g,
    (matched, boundary, letter) => boundary + letter.toUpperCase(),
  );
};

// Paragraphs longer than this ramble — they get the full rewrite instead
// of the surgical copyedit.
const REWRITE_THRESHOLD = 280;

// A deterministic stand-in for an LLM rewrite: keep the opening sentence,
// replace everything after it with two crisp sentences built around
// keywords lifted from the dropped text. Produces the large, visible
// rewrites a real agent would.
const rewriteText = (text) => {
  const sentences = text
    .replace(/\s+/g, ' ')
    .trim()
    .match(/[^.!?]+[.!?]*/g) || [text];
  const lead = sentences[0].trim();
  const rest = sentences.slice(1).join(' ').trim();
  const keywords = (rest || lead)
    .split(/\s+/)
    .map((word) => word.replace(/[^\p{L}\p{N}-]/gu, ''))
    .filter((word) => word.length > 5)
    .slice(0, 2);
  const focus = keywords.length
    ? keywords.join(' and ').toLowerCase()
    : 'the essentials';
  return (
    `${lead}${/[.!?:]$/.test(lead) ? '' : '.'} ` +
    `The key point is ${focus} — everything else on the page supports ` +
    `it. Said once and said clearly, visitors take it in on the first read.`
  );
};

// Deterministic stand-in for an LLM cleaning up legacy markup: strip
// presentational attributes and tags that belong in the theme, not the
// content.
const cleanupHtml = (html) =>
  html
    .replace(/<\/?font[^>]*>/gi, '')
    .replace(
      /\s(?:style|bgcolor|border|cellpadding|cellspacing|width|align|color|size)="[^"]*"/gi,
      '',
    )
    .replace(/&nbsp;/g, ' ')
    .replace(/[ \t]{2,}/g, ' ');

// Deterministic stand-in for the agent picking a stronger visual from the
// site's asset library: any image block not already showing the library
// pick gets swapped (data URLs the user just attached are left alone).
const LIBRARY_IMAGE = {
  url: '/sunrise-ridge.svg',
  alt: 'Sunrise over a mountain ridge',
};

// Deterministic stand-in for an LLM generating a picture: the "result" is
// pre-rendered and served from the site, but the interaction — a visibly
// long-running generation step, then placing the new block — mirrors the
// real agent.
const GENERATED_IMAGE = {
  url: '/next-steps-illustration.svg',
  alt: 'Abstract illustration of ascending steps',
};

const tableCell = (text, type) => ({
  key: uid().slice(0, 8),
  type,
  value: [{ type: 'p', children: [{ text }] }],
});

const tableRow = (cells, type) => ({
  key: uid().slice(0, 8),
  cells: cells.map((text) => tableCell(text, type)),
});

// Only the written text changes — the block keeps its node type (p, h2, …)
// and every other field (styles, alignment, custom settings).
const updateSlateText = (state, blockId, text) => {
  const block = state.blocks[blockId] || { '@type': 'slate' };
  const type = block.value?.[0]?.type || 'p';
  return {
    ...state,
    blocks: {
      ...state.blocks,
      [blockId]: {
        ...block,
        value: [{ type, children: [{ text }] }],
        plaintext: text,
      },
    },
  };
};

// A sign-off line: a dash-led slate paragraph ("– The Team").
const isSignOff = (block) =>
  block?.['@type'] === 'slate' && /^[–—-]/.test(blockText(block).trim());

const describeStructure = (state) => {
  const items = layoutItems(state);
  if (!items.length) {
    return 'The page has no blocks yet.';
  }
  const counts = {};
  items.forEach((id) => {
    const type = state.blocks[id]?.['@type'] || 'unknown';
    counts[type] = (counts[type] || 0) + 1;
  });
  const parts = Object.entries(counts)
    .map(([type, count]) => `${count} ${type}`)
    .join(', ');
  const order = items
    .map((id) => state.blocks[id]?.['@type'] || 'unknown')
    .join(' → ');
  return (
    `The page has ${items.length} block${items.length === 1 ? '' : 's'}: ` +
    `${parts}.\n\nOrder from top to bottom: ${order}.`
  );
};

const firstSentence = (text) => {
  const match = text.match(/^.*?[.!?](\s|$)/);
  const sentence = match ? match[0].trim() : text.trim();
  return sentence.length > 140 ? `${sentence.slice(0, 137).trim()}…` : sentence;
};

const extractTopic = (message) => {
  const match = message.match(/\b(?:about|on|über|zum thema|zu)\s+(.+)$/i);
  if (!match) return null;
  return match[1].replace(/[.!?]\s*$/, '').trim();
};

const titleCase = (text) =>
  text.replace(/(^|\s)\S/g, (letter) => letter.toUpperCase());

// ---------------------------------------------------------------------------
// Scenarios
// ---------------------------------------------------------------------------

const DENIED_REPLY =
  'This turn only had read permission, so I did not change anything. ' +
  'Use "Send again with Write" below — or flip the toggle to "Write" and ' +
  'resend the request yourself.';

const deny = (attemptedTool, description) => ({
  steps: [{ name: attemptedTool, description, blocked: true }],
  reply: DENIED_REPLY,
  newState: null,
  denied: true,
});

const needsSelection = (what) => ({
  steps: [{ name: 'get_layout', description: 'Reading the page layout' }],
  reply:
    `I need to know which block you mean. Click the block you want ` +
    `to ${what} in the page, then send the request again — I will see ` +
    `your selection.`,
  newState: null,
});

// Shared by the attachment and reference scenarios: one teaser card per
// picked site content, appended to the page.
const insertTeaser = (state, reference) =>
  insertBlock(state, {
    '@type': 'teaser',
    href: [
      {
        '@id': reference['@id'],
        '@type': reference.type,
        title: reference.title,
        hasPreviewImage: false,
        image_field: '',
      },
    ],
    title: reference.title,
    description: `Read more about ${reference.title}.`,
  });

const SCENARIOS = [
  {
    // Attached/pasted images: insert them into the page as image blocks.
    // References picked alongside them are placed too, as teaser cards —
    // nothing the user handed over is silently dropped.
    match: (lower, request) => (request.attachments || []).length > 0,
    build: ({ state, permission, attachments, references }) => {
      if (permission !== 'write') {
        return deny('create_element', 'Adding the image — blocked');
      }
      let current = state;
      const steps: any[] = [
        { name: 'get_layout', description: 'Reading the page layout' },
      ];
      attachments.forEach((attachment, index) => {
        const inserted = insertBlock(current, {
          '@type': 'image',
          url: attachment.dataUrl,
          alt: attachment.name || '',
          align: 'center',
        });
        current = inserted.state;
        steps.push({
          name: 'create_element',
          description:
            attachments.length > 1
              ? `Adding image ${index + 1} of ${attachments.length}`
              : 'Adding the image to the page',
          state: current,
          blockId: inserted.id,
        });
      });
      (references || []).forEach((reference) => {
        const inserted = insertTeaser(current, reference);
        current = inserted.state;
        steps.push({
          name: 'create_element',
          description: `Adding a teaser for "${reference.title}"`,
          state: current,
          blockId: inserted.id,
        });
      });
      const count = attachments.length;
      const referenceCount = (references || []).length;
      return {
        steps,
        reply:
          `I added ${count === 1 ? 'the image' : `${count} images`} to the ` +
          `end of the page as ${count === 1 ? 'an image block' : 'image blocks'}` +
          `${
            referenceCount
              ? `, and linked the ${
                  referenceCount === 1
                    ? 'referenced page'
                    : `${referenceCount} referenced pages`
                } below ${referenceCount === 1 ? 'it' : 'them'} as teaser ${
                  referenceCount === 1 ? 'card' : 'cards'
                }`
              : ''
          }. ` +
          `Select ${count === 1 ? 'it' : 'one'} to adjust size and ` +
          `alignment, or tell me where to move ${count === 1 ? 'it' : 'them'}.`,
        newState: current,
      };
    },
  },
  {
    // Referenced site content: link it from the page as teaser cards.
    match: (lower, request) => (request.references || []).length > 0,
    build: ({ state, permission, references }) => {
      if (permission !== 'write') {
        return deny('create_element', 'Adding the linked content — blocked');
      }
      let current = state;
      const steps: any[] = [
        { name: 'get_layout', description: 'Reading the page layout' },
      ];
      references.forEach((reference) => {
        const inserted = insertTeaser(current, reference);
        current = inserted.state;
        steps.push({
          name: 'create_element',
          description: `Adding a teaser for "${reference.title}"`,
          state: current,
          blockId: inserted.id,
        });
      });
      const count = references.length;
      return {
        steps,
        reply:
          `I linked the ${count === 1 ? 'referenced page' : `${count} referenced pages`} ` +
          `at the end of this page as teaser ${count === 1 ? 'card' : 'cards'}. ` +
          `Each card points at the content you picked — tell me to move ` +
          `${count === 1 ? 'it' : 'them'} or change the wording.`,
        newState: current,
      };
    },
  },
  {
    // Whole-page makeover: one long multi-step sweep chaining several
    // operations (insert, per-block updates, more inserts), so the live
    // progress affordances — agent cursor, step list — have something to
    // show off.
    match: (lower) =>
      /\b(makeover|overhaul|rework|restructur\w*|tidy\w*|polish\w*|aufräum\w*|überarbeit\w*|aufhübsch\w*)\b/.test(
        lower,
      ),
    build: ({ state, permission }) => {
      if (permission !== 'write') {
        return deny('update_element', 'Reworking the page — blocked');
      }
      let current = state;
      const events = [];
      const tool = (step) => events.push({ kind: 'tool', step });
      const say = (text) => events.push({ kind: 'text', text });

      // Pass 1: structure.
      say(
        "I'll work in three passes — structure, copy, then media. " +
          'Starting with the structure.',
      );
      tool({ name: 'get_layout', description: 'Reading the page layout' });
      const hasToc = layoutItems(current).some(
        (id) => current.blocks[id]?.['@type'] === 'toc',
      );
      let addedToc = false;
      if (!hasToc) {
        const toc = insertBlock(
          current,
          { '@type': 'toc', variation: 'default' },
          'top',
        );
        current = toc.state;
        addedToc = true;
        tool({
          name: 'create_element',
          description: 'Adding a table of contents below the header',
          state: current,
          blockId: toc.id,
        });
      }

      // Lead with the strongest visual: an image sitting further down
      // moves to the top of the page. The makeover's own furniture is not
      // raw material — the generated closing illustration is no hero
      // candidate, and a summary from an earlier run stays above the hero
      // without counting as "content in front of it".
      const heroItems = layoutItems(current);
      const heroImageId = heroItems.find((id) => {
        const block = current.blocks[id];
        return (
          block?.['@type'] === 'image' &&
          !block.url?.includes?.(GENERATED_IMAGE.url)
        );
      });
      const heroSlot = heroItems.findIndex((id, index) => {
        if (index < firstBodyIndex(current, heroItems)) return false;
        const block = current.blocks[id];
        return !(
          block?.['@type'] === 'slate' &&
          /^In short:/.test(blockText(block).trim())
        );
      });
      let movedHero = false;
      if (heroImageId && heroItems[heroSlot] !== heroImageId) {
        current = moveBlock(current, heroImageId, 'top');
        movedHero = true;
        tool({
          name: 'move_element',
          description: 'Moving the image up to lead the page',
          state: current,
          blockId: heroImageId,
        });
      }

      // A sign-off line (dash-led paragraph) belongs at the very end; if
      // it sits above other content, it moves down — with one neighbour
      // that is a plain swap.
      const signItems = layoutItems(current);
      const signOffId = signItems.find((id) => isSignOff(current.blocks[id]));
      const signOffIndex = signItems.indexOf(signOffId);
      let movedSignOff = false;
      if (signOffId && signOffIndex !== signItems.length - 1) {
        current = moveBlock(current, signOffId, 'bottom');
        movedSignOff = true;
        tool({
          name: 'move_element',
          description:
            signOffIndex === signItems.length - 2
              ? 'Swapping the sign-off and the closing note'
              : 'Moving the sign-off to the end of the page',
          state: current,
          blockId: signOffId,
        });
      }

      // Pass 2: copy — every paragraph gets a tighter version, plus a
      // summary at the top. A summary from an earlier makeover is not
      // body copy — it neither gets tightened nor summarized again.
      const paragraphIds = layoutItems(current).filter((id) => {
        const block = current.blocks[id];
        return (
          block?.['@type'] === 'slate' &&
          block.value?.[0]?.type === 'p' &&
          blockText(block).trim() &&
          !/^In short:/.test(blockText(block).trim())
        );
      });
      say(
        paragraphIds.length
          ? `Structure is set. Now the copy — I'll go through all ` +
              `${paragraphIds.length} paragraphs: word-level edits where ` +
              `the text already works, full rewrites where it rambles. ` +
              `Then a summary at the top.`
          : 'Structure is set. There are no body paragraphs to tighten, ' +
              'so on to the rest.',
      );
      let updated = 0;
      paragraphIds.forEach((id, index) => {
        const original = blockText(current.blocks[id]);
        const cleaned = cleanupText(original);
        const rewritten =
          cleaned.length > REWRITE_THRESHOLD
            ? rewriteText(cleaned)
            : copyEditText(cleaned);
        // An idempotent rewrite (e.g. on an already made-over paragraph)
        // is a read, not an update — claiming a change with an empty diff
        // would be dishonest.
        if (
          rewritten.replace(/\s+/g, ' ').trim() ===
          original.replace(/\s+/g, ' ').trim()
        ) {
          tool({
            name: 'get_element',
            description: `Checking paragraph ${index + 1} of ${paragraphIds.length}`,
            blockId: id,
          });
          return;
        }
        current = updateSlateText(current, id, rewritten);
        updated += 1;
        tool({
          name: 'update_element',
          description: `Updating paragraph ${index + 1} of ${paragraphIds.length}`,
          state: current,
          blockId: id,
          before: original,
          after: rewritten,
        });
      });
      const hasSummary = layoutItems(current).some((id) => {
        const block = current.blocks[id];
        return (
          block?.['@type'] === 'slate' &&
          /^In short:/.test(blockText(block).trim())
        );
      });
      const sentences = paragraphIds
        .slice(0, 2)
        .map((id) => firstSentence(blockText(current.blocks[id])));
      let addedSummary = false;
      if (!hasSummary && sentences.length) {
        const summary = insertBlock(
          current,
          slateBlock(`In short: ${sentences.join(' ')}`),
          'top',
        );
        current = summary.state;
        addedSummary = true;
        tool({
          name: 'create_element',
          description: 'Writing a summary at the top',
          state: current,
          blockId: summary.id,
        });
      }

      // Pass 3: media — swap tired imagery for the library pick, alt text
      // for images, alignment for videos, and legacy markup in HTML blocks.
      const mediaSteps = [];
      layoutItems(current).forEach((id) => {
        const block = current.blocks[id];
        if (block?.['@type'] === 'image') {
          if (
            block.url &&
            !block.url.startsWith('data:') &&
            !block.url.includes(LIBRARY_IMAGE.url) &&
            !block.url.includes(GENERATED_IMAGE.url)
          ) {
            // Scale metadata (srcset hashes) belongs to the old picture —
            // carrying it over would render broken scaled URLs.
            const { image_scales, ...rest } = block;
            current = {
              ...current,
              blocks: {
                ...current.blocks,
                [id]: {
                  ...rest,
                  url: LIBRARY_IMAGE.url,
                  alt: LIBRARY_IMAGE.alt,
                },
              },
            };
            mediaSteps.push({
              name: 'update_element',
              description: 'Swapping the image for a stronger visual',
              state: current,
              blockId: id,
              image: { before: block.url, after: LIBRARY_IMAGE.url },
            });
          } else if (!block.alt) {
            const alt = `Illustration: ${current.title || 'this page'}`;
            current = {
              ...current,
              blocks: { ...current.blocks, [id]: { ...block, alt } },
            };
            mediaSteps.push({
              name: 'update_element',
              description: 'Updating the image alt text',
              state: current,
              blockId: id,
              before: '',
              after: alt,
            });
          }
        } else if (block?.['@type'] === 'video' && block.align !== 'center') {
          current = {
            ...current,
            blocks: {
              ...current.blocks,
              [id]: { ...block, align: 'center' },
            },
          };
          mediaSteps.push({
            name: 'update_element',
            description: 'Centering the video',
            state: current,
            blockId: id,
          });
        } else if (block?.['@type'] === 'html' && block.html) {
          const cleaned = cleanupHtml(block.html);
          if (cleaned !== block.html) {
            current = {
              ...current,
              blocks: {
                ...current.blocks,
                [id]: { ...block, html: cleaned },
              },
            };
            mediaSteps.push({
              name: 'update_element',
              description: 'Updating the HTML block markup',
              state: current,
              blockId: id,
              before: block.html,
              after: cleaned,
            });
          }
        }
      });
      if (mediaSteps.length) {
        say(
          'Copy is done. On to the media — images, video and embedded ' +
            'markup.',
        );
        mediaSteps.forEach(tool);
      }

      // Closing section, with a generated illustration. A sign-off that
      // (now) closes the page stays the very last block — the section
      // slots in above it. A "Next Steps" section from an earlier
      // makeover means this pass has nothing to add.
      const hasClosing = layoutItems(current).some((id) => {
        const block = current.blocks[id];
        return (
          block?.['@type'] === 'slate' &&
          block.value?.[0]?.type === 'h2' &&
          blockText(block).trim() === 'Next Steps'
        );
      });
      let addedClosing = false;
      let generated = false;
      if (!hasClosing) {
        say(
          'Almost there — wrapping up with a closing section, and ' +
            'generating an illustration for it. Generation takes a moment.',
        );
        const closingIndex = () => {
          const items = layoutItems(current);
          return items.length &&
            isSignOff(current.blocks[items[items.length - 1]])
            ? items.length - 1
            : items.length;
        };
        const heading = insertBlockAt(
          current,
          slateBlock('Next Steps', 'h2'),
          closingIndex(),
        );
        current = heading.state;
        addedClosing = true;
        tool({
          name: 'create_element',
          description: 'Creating the closing section heading',
          state: current,
          blockId: heading.id,
        });
        const hasIllustration = layoutItems(current).some((id) =>
          current.blocks[id]?.url?.includes?.(GENERATED_IMAGE.url),
        );
        if (!hasIllustration) {
          tool({
            name: 'generate_image',
            description: 'Generating an illustration for the closing section',
            duration: 3400,
          });
          const illustration = insertBlockAt(
            current,
            {
              '@type': 'image',
              url: GENERATED_IMAGE.url,
              image_field: 'image',
              alt: GENERATED_IMAGE.alt,
              align: 'center',
            },
            closingIndex(),
          );
          current = illustration.state;
          generated = true;
          tool({
            name: 'create_element',
            description: 'Placing the generated illustration',
            state: current,
            blockId: illustration.id,
            image: { after: GENERATED_IMAGE.url },
          });
        }
        const body = insertBlockAt(
          current,
          slateBlock(
            'This closing section was drafted by the assistant — tell ' +
              'visitors what to do next, or ask me to rewrite it.',
          ),
          closingIndex(),
        );
        current = body.state;
        tool({
          name: 'create_element',
          description: 'Drafting the closing paragraph',
          state: current,
          blockId: body.id,
        });
      }

      // The reply enumerates what actually happened this run — a second
      // makeover on an already made-over page honestly reports a no-op.
      const changed = events.some(
        (event) => event.kind === 'tool' && event.step.state,
      );
      const did = [];
      if (addedToc) did.push('added a table of contents below the header');
      if (movedHero) did.push('moved the image up to lead the page');
      if (movedSignOff) did.push('moved the sign-off to the end');
      if (updated > 0) {
        did.push(
          `tightened ${
            updated === paragraphIds.length
              ? `all ${paragraphIds.length}`
              : `${updated} of the ${paragraphIds.length}`
          } paragraphs`,
        );
      } else if (paragraphIds.length) {
        did.push(`found all ${paragraphIds.length} paragraphs already tight`);
      }
      if (addedSummary) did.push('put a short summary at the top');
      if (mediaSteps.length) {
        did.push(
          `tidied ${mediaSteps.length} media block${
            mediaSteps.length === 1 ? '' : 's'
          }`,
        );
      }
      if (addedClosing) {
        did.push(
          `drafted a "Next Steps" section${
            generated ? ' with a generated illustration' : ''
          }`,
        );
      }
      if (changed) {
        const summaryText =
          did.length === 1
            ? did[0]
            : `${did.slice(0, -1).join(', ')} and ${did[did.length - 1]}`;
        say(
          `Makeover done. I ${summaryText}. Review the working copy and ` +
            `undo anything you do not like.`,
        );
      } else {
        say(
          'I went over structure, copy and media — this page already has ' +
            'the full makeover treatment, so I changed nothing.',
        );
      }
      return { events, newState: changed ? current : null };
    },
  },
  {
    // Delete the selected block.
    match: (lower) => /\b(delete|remove|lösch\w*|entfern\w*)\b/.test(lower),
    build: ({ state, context, permission }) => {
      if (!context?.block_id || !state.blocks[context.block_id]) {
        return needsSelection('delete');
      }
      if (permission !== 'write') {
        return deny('remove_element', 'Removing the block — blocked');
      }
      const type = state.blocks[context.block_id]['@type'];
      const next = removeBlock(state, context.block_id);
      return {
        steps: [
          { name: 'get_layout', description: 'Reading the page layout' },
          {
            name: 'remove_element',
            description: `Removing the ${type} block`,
            state: next,
          },
        ],
        reply:
          `I removed the ${type} block you selected. The change only ` +
          `affects the working copy — undo it with Ctrl+Z or discard ` +
          `everything with Cancel.`,
        newState: next,
      };
    },
  },
  {
    // Duplicate the selected block.
    match: (lower) => /\b(duplicate|duplizier\w*|kopie|clone)\b/.test(lower),
    build: ({ state, context, permission }) => {
      if (!context?.block_id || !state.blocks[context.block_id]) {
        return needsSelection('duplicate');
      }
      if (permission !== 'write') {
        return deny('create_element', 'Duplicating the block — blocked');
      }
      const type = state.blocks[context.block_id]['@type'];
      const copyId = uid();
      const items = layoutItems(state);
      const nextItems = [...items];
      nextItems.splice(items.indexOf(context.block_id) + 1, 0, copyId);
      const next = withBlocks(
        state,
        {
          ...state.blocks,
          [copyId]: JSON.parse(JSON.stringify(state.blocks[context.block_id])),
        },
        nextItems,
      );
      return {
        steps: [
          { name: 'get_layout', description: 'Reading the page layout' },
          {
            name: 'create_element',
            description: `Duplicating the ${type} block`,
            state: next,
            blockId: copyId,
          },
        ],
        reply:
          `I duplicated the ${type} block; the copy sits directly below ` +
          `the original. Edit it in place, or tell me where to move it.`,
        newState: next,
      };
    },
  },
  {
    // Move the selected block: top, bottom, or one step up/down. A bare
    // placement phrase ("… to the top") only reads as a move when the
    // sentence is not a creation request ("add a summary to the top").
    match: (lower) =>
      /\b(move|verschieb\w*|reorder|sortier\w*)\b/.test(lower) ||
      (/\b(nach oben|nach unten|to the top|to the end|to the bottom)\b/.test(
        lower,
      ) &&
        !/\b(add|insert|create|write|füg\w*|erstell\w*|schreib\w*)\b/.test(
          lower,
        )),
    build: ({ message, state, context, permission }) => {
      if (!context?.block_id || !state.blocks[context.block_id]) {
        return needsSelection('move');
      }
      if (permission !== 'write') {
        return deny('move_element', 'Moving the block — blocked');
      }
      const lower = message.toLowerCase();
      const oneStep = /\b(one|a bit|slightly|eins|etwas)\b|\bstep\b/.test(
        lower,
      );
      let placement;
      if (/\b(up|top|oben|anfang|hoch)\b/.test(lower)) {
        placement = oneStep ? 'up' : 'top';
      } else {
        placement = oneStep ? 'down' : 'bottom';
      }
      const labels = {
        top: 'to the top, right below the page header',
        bottom: 'to the end of the page',
        up: 'one position up',
        down: 'one position down',
      };
      const type = state.blocks[context.block_id]['@type'];
      const next = moveBlock(state, context.block_id, placement);
      return {
        steps: [
          { name: 'get_layout', description: 'Reading the page layout' },
          {
            name: 'move_element',
            description: `Moving the ${type} block ${labels[placement]}`,
            state: next,
            blockId: context.block_id,
          },
        ],
        reply: `Done — I moved the ${type} block ${labels[placement]}.`,
        newState: next,
      };
    },
  },
  {
    // Page-wide proofreading sweep over the text blocks.
    match: (lower) =>
      /\b(proofread|typo\w*|spelling|korrektur|rechtschreib\w*|korrigier\w*)\b/.test(
        lower,
      ),
    build: ({ state, permission }) => {
      if (permission !== 'write') {
        return deny('update_element', 'Correcting the text blocks — blocked');
      }
      const paragraphIds = layoutItems(state).filter((id) => {
        const block = state.blocks[id];
        return (
          block?.['@type'] === 'slate' &&
          block.value?.[0]?.type === 'p' &&
          blockText(block).trim()
        );
      });
      if (!paragraphIds.length) {
        return {
          steps: [
            { name: 'get_layout', description: 'Reading the page layout' },
          ],
          reply: 'There are no text paragraphs to proofread on this page.',
          newState: null,
        };
      }
      let current = state;
      let fixed = 0;
      const steps: any[] = [
        { name: 'get_layout', description: 'Reading the page layout' },
      ];
      paragraphIds.forEach((id, index) => {
        const original = blockText(current.blocks[id]);
        const cleaned = cleanupText(original);
        const step: any = {
          name: 'update_element',
          description: `Checking paragraph ${index + 1} of ${paragraphIds.length}`,
          blockId: id,
        };
        if (cleaned !== original) {
          current = updateSlateText(current, id, cleaned);
          fixed += 1;
          step.description = `Updating paragraph ${index + 1} of ${paragraphIds.length}`;
          step.state = current;
          step.before = original;
          step.after = cleaned;
        }
        steps.push(step);
      });
      return {
        steps,
        reply:
          fixed > 0
            ? `I went through all ${paragraphIds.length} paragraphs and ` +
              `fixed ${fixed} of them — stray spaces, missing end ` +
              `punctuation and casing. The rest were already clean.`
            : `I checked all ${paragraphIds.length} paragraphs — spacing, ` +
              `punctuation and casing are already consistent, so I ` +
              `changed nothing.`,
        newState: fixed > 0 ? current : null,
      };
    },
  },
  {
    // Shorten or rewrite the selected text block.
    match: (lower) =>
      /\b(shorten|rewrite|kürz\w*|umschreib\w*|improve|verbesser\w*)\b/.test(
        lower,
      ),
    build: ({ state, context, permission }) => {
      const block = context?.block_id ? state.blocks[context.block_id] : null;
      if (!block || block['@type'] !== 'slate') {
        return needsSelection('rewrite');
      }
      if (permission !== 'write') {
        return deny('update_element', 'Updating the text — blocked');
      }
      const original = blockText(block);
      if (!original.trim()) {
        return {
          steps: [
            { name: 'get_layout', description: 'Reading the selected block' },
          ],
          reply:
            'The selected text block is empty — there is nothing to rewrite.',
          newState: null,
        };
      }
      const shortened = firstSentence(original);
      // A paragraph that already is its first sentence has nothing to
      // shed — claiming a change with an empty diff would be dishonest.
      if (
        shortened.replace(/\s+/g, ' ').trim() ===
        original.replace(/\s+/g, ' ').trim()
      ) {
        return {
          steps: [
            { name: 'get_layout', description: 'Reading the selected block' },
            { name: 'get_element', description: 'Reading the text block' },
          ],
          reply:
            'The selected paragraph is already a single tight statement — ' +
            'shortening it further would cut meaning, so I left it as is.',
          newState: null,
        };
      }
      const next = updateSlateText(state, context.block_id, shortened);
      return {
        steps: [
          { name: 'get_layout', description: 'Reading the selected block' },
          {
            name: 'update_element',
            description: 'Updating the text block',
            state: next,
            blockId: context.block_id,
            before: original,
            after: shortened,
          },
        ],
        reply:
          `I tightened the selected text to its core statement. The ` +
          `previous wording stays available via undo (Ctrl+Z).`,
        newState: next,
      };
    },
  },
  {
    // Expand the selected text block.
    match: (lower) =>
      /\b(expand|lengthen|elaborate|erweiter\w*|ausführlich\w*|verlänger\w*)\b/.test(
        lower,
      ),
    build: ({ state, context, permission }) => {
      const block = context?.block_id ? state.blocks[context.block_id] : null;
      if (!block || block['@type'] !== 'slate') {
        return needsSelection('expand');
      }
      if (permission !== 'write') {
        return deny('update_element', 'Expanding the text — blocked');
      }
      const original = blockText(block).trim();
      if (!original) {
        return {
          steps: [
            { name: 'get_layout', description: 'Reading the selected block' },
          ],
          reply:
            'The selected text block is empty — there is nothing to expand.',
          newState: null,
        };
      }
      const expanded =
        `${original} This matters in practice: it shapes how visitors ` +
        `find their way around and what they take away from the page. ` +
        `A concrete example makes the point tangible.`;
      const next = updateSlateText(state, context.block_id, expanded);
      return {
        steps: [
          { name: 'get_layout', description: 'Reading the selected block' },
          {
            name: 'update_element',
            description: 'Expanding the text block',
            state: next,
            blockId: context.block_id,
            before: original,
            after: expanded,
          },
        ],
        reply:
          `I extended the selected paragraph with two follow-up ` +
          `sentences. They are generic on purpose — tell me the angle ` +
          `(audience, example, numbers) and I will make them specific.`,
        newState: next,
      };
    },
  },
  {
    // Table of contents (must match before the generic table scenario).
    match: (lower) =>
      /\b(table of contents|toc|inhaltsverzeichnis)\b/.test(lower),
    build: ({ state, permission }) => {
      if (permission !== 'write') {
        return deny('create_element', 'Adding a table of contents — blocked');
      }
      const inserted = insertBlock(
        state,
        { '@type': 'toc', variation: 'default' },
        'top',
      );
      return {
        steps: [
          { name: 'get_layout', description: 'Reading the page layout' },
          {
            name: 'create_element',
            description: 'Adding the table of contents',
            state: inserted.state,
            blockId: inserted.id,
          },
        ],
        reply:
          'I added a table of contents right below the page header. It ' +
          'builds itself from the headings on the page, so it stays ' +
          'current as sections change.',
        newState: inserted.state,
      };
    },
  },
  {
    // Insert a sample table.
    match: (lower) =>
      /\b(add|insert|create|füg\w*|erstell\w*)\b/.test(lower) &&
      /\b(table|tabelle)\b/.test(lower),
    build: ({ message, state, permission }) => {
      if (permission !== 'write') {
        return deny('create_element', 'Creating a table — blocked');
      }
      const topic = extractTopic(message) || 'opening hours';
      const inserted = insertBlock(state, {
        '@type': 'slateTable',
        table: {
          hideHeaders: false,
          fixed: true,
          compact: false,
          basic: false,
          celled: true,
          inverted: false,
          striped: false,
          rows: [
            tableRow(['Day', titleCase(topic)], 'header'),
            tableRow(['Monday – Friday', '9:00 – 18:00'], 'data'),
            tableRow(['Saturday', '10:00 – 14:00'], 'data'),
            tableRow(['Sunday', 'Closed'], 'data'),
          ],
        },
      });
      return {
        steps: [
          { name: 'get_layout', description: 'Reading the page layout' },
          {
            name: 'create_element',
            description: 'Creating the table',
            state: inserted.state,
            blockId: inserted.id,
          },
        ],
        reply:
          `I added a table for ${topic} with a header row and three data ` +
          `rows at the end of the page. The values are placeholders — ` +
          `edit the cells directly or tell me what to put in them.`,
        newState: inserted.state,
      };
    },
  },
  {
    // Embed a video (URL from the message, or a sample clip).
    match: (lower) => /\b(video|film|clip)\b/.test(lower),
    build: ({ message, state, permission }) => {
      if (permission !== 'write') {
        return deny('create_element', 'Embedding a video — blocked');
      }
      const urlMatch = message.match(/https?:\/\/\S+/);
      const url = urlMatch
        ? urlMatch[0].replace(/[.,;)]$/, '')
        : 'https://interactive-examples.mdn.mozilla.net/media/cc0-videos/flower.mp4';
      const inserted = insertBlock(state, {
        '@type': 'video',
        url,
        align: 'center',
      });
      return {
        steps: [
          { name: 'get_layout', description: 'Reading the page layout' },
          {
            name: 'create_element',
            description: 'Embedding the video',
            state: inserted.state,
            blockId: inserted.id,
          },
        ],
        reply: urlMatch
          ? 'I embedded the video from the link you gave at the end of the page.'
          : 'I embedded a sample video at the end of the page — paste a ' +
            'YouTube, Vimeo or file URL into the request and I will use ' +
            'that instead.',
        newState: inserted.state,
      };
    },
  },
  {
    // Summary paragraph at the top of the page.
    match: (lower) =>
      /\b(add|insert|create|write|füg\w*|erstell\w*|schreib\w*)\b/.test(
        lower,
      ) && /\b(summary|zusammenfassung|tl;?dr)\b/.test(lower),
    build: ({ state, permission }) => {
      if (permission !== 'write') {
        return deny('create_element', 'Adding a summary — blocked');
      }
      const sentences = layoutItems(state)
        .map((id) => state.blocks[id])
        .filter(
          (block) =>
            block?.['@type'] === 'slate' &&
            block.value?.[0]?.type === 'p' &&
            blockText(block).trim(),
        )
        .slice(0, 2)
        .map((block) => firstSentence(blockText(block)));
      if (!sentences.length) {
        return {
          steps: [
            { name: 'get_layout', description: 'Reading the page layout' },
          ],
          reply:
            'There is no body text to summarize yet — add some content ' +
            'first, or ask me to draft a section.',
          newState: null,
        };
      }
      const summary = `In short: ${sentences.join(' ')}`;
      const inserted = insertBlock(state, slateBlock(summary), 'top');
      return {
        steps: [
          { name: 'get_layout', description: 'Reading the page layout' },
          {
            name: 'create_element',
            description: 'Writing the summary at the top',
            state: inserted.state,
            blockId: inserted.id,
          },
        ],
        reply:
          'I added a one-paragraph summary right below the page header, ' +
          'built from the leading statements of the page. Sharpen the ' +
          'wording as you like, or ask me to rewrite it.',
        newState: inserted.state,
      };
    },
  },
  {
    // Add a new text section.
    match: (lower) =>
      /\b(add|insert|create|write|füg\w*|erstell\w*|schreib\w*)\b/.test(
        lower,
      ) &&
      /\b(section|abschnitt|paragraph|absatz|text|intro\w*|example|beispiel)\b/.test(
        lower,
      ),
    build: ({ message, state, permission }) => {
      if (permission !== 'write') {
        return deny('create_element', 'Creating a text section — blocked');
      }
      const topic = extractTopic(message) || 'this page';
      const heading = titleCase(topic);
      const body =
        `This section introduces ${topic}. It was drafted by the ` +
        `assistant as a starting point — adjust the wording to match ` +
        `your tone, or ask me to rewrite it.`;
      const withHeading = insertBlock(state, slateBlock(heading, 'h2'));
      const withBody = insertBlock(withHeading.state, slateBlock(body));
      return {
        steps: [
          { name: 'get_layout', description: 'Reading the page layout' },
          {
            name: 'create_element',
            description: `Creating the heading "${heading}"`,
            state: withHeading.state,
            blockId: withHeading.id,
          },
          {
            name: 'create_element',
            description: 'Creating the paragraph',
            state: withBody.state,
            blockId: withBody.id,
          },
        ],
        reply:
          `I added a new section "${heading}" with a heading and an ` +
          `introductory paragraph at the end of the page. Review it and ` +
          `keep, edit, or undo it.`,
        newState: withBody.state,
      };
    },
  },
  {
    // Read-only questions about the page.
    match: (lower) =>
      /\b(what|which|how many|summar\w*|describe|structure|welche|wie viele|was|zeig\w*|überblick|beschreib\w*)\b/.test(
        lower,
      ),
    build: ({ state }) => ({
      steps: [
        { name: 'get_layout', description: 'Reading the page layout' },
        { name: 'get_metadata', description: 'Reading the page metadata' },
      ],
      reply: describeStructure(state),
      newState: null,
    }),
  },
];

const FALLBACK = {
  steps: [{ name: 'get_layout', description: 'Reading the page layout' }],
  reply:
    'I can answer questions about this page and edit it for you. Try:\n' +
    '- "What is on this page?"\n' +
    '- "Add a section about our opening hours", "add a table", ' +
    '"add a video", "add a table of contents", "add a summary"\n' +
    '- "Proofread the page" or "give this page a makeover"\n' +
    '- Select a block, then "move this to the top" (or "up one"), ' +
    '"shorten this", "expand this", "duplicate this" or "delete this"\n' +
    '- Attach or paste an image, or pick site content with the link ' +
    'button, and I will place it on the page\n\n' +
    'Reading is always allowed; edits need the pen toggle switched on ' +
    'for that message.',
  newState: null,
};

const buildResponse = (request: any): any => {
  const lower = request.message.toLowerCase();
  const scenario = SCENARIOS.find((candidate) =>
    candidate.match(lower, request),
  );
  return scenario ? scenario.build(request) : FALLBACK;
};

export { buildResponse, uid, STEP_DELAY, TOKEN_DELAY };
